import { useParams } from "react-router-dom";
import { CalenderIcon, HomeIcon, NUSDIcon } from "../../AssetComponents/Images";
import { Toggle } from "../../Components/Toggle/Toggle";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../functions/Api";
import { updateNFTStore } from "../../ReduxSlices/NFTSlice";
import Modal from "react-responsive-modal";
import { useEffect, useState, useCallback } from "react";
import addDays from "date-fns/addDays";
import { setReservationPeriod } from "../../ReduxSlices/ReservationSlice";
import { PurpleButton } from "../Buttons/PurpleButton";
import { Mainnet } from "@nibiruchain/nibijs";
import { updateToken } from "../functions/Functions";
import { isSameDayBookingAllowed } from "../utils/DateValidation";

export const PickDateWithBlockedDates = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.nft.nfts[params.id]);

  const mainnet = Mainnet();
  const getToken = async () => {
    if (!token) {
      try {
        const propertyData = await api("property/getProperty", {
          token_id: params.id,
        });
        if (propertyData) {
          dispatch(
            updateNFTStore({
              token_id: params.id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals,
              access: propertyData.access,
              icalSync: propertyData.icalSync || null,
              manuallyBlockedDates: propertyData.manuallyBlockedDates || [],
            })
          );
        }
      } catch (error) {
        console.error("Failed to get property from backend:", error);
        await updateToken(
          {
            token_id: params.id,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      }
    }
  };

  useEffect(() => {
    getToken();
  }, []);

  const reservation_period = useSelector((state) => state.reservation.period);
  const [disabledDates, setDisabledDates] = useState([]);
  const [blockedDateRanges, setBlockedDateRanges] = useState([]);
  const [showModal, setShowModal] = useState(false);
   const [showBlockedDatesWarningModal, setShowBlockedDatesWarningModal] = useState(false);
  const [blockedDatesInRange, setBlockedDatesInRange] = useState([]);
  const [firstDay, setFirstDay] = useState();
  const [loadingBlockedDates, setLoadingBlockedDates] = useState(false);
  const diffToUTC = useSelector((state) => state.time.diffToUTC);
  
  // Helper function to get default dates (ensuring at least 1 night stay)
  // Uses 11:30 cutoff: after 11:30 AM, same-day check-in is not allowed
  const getDefaultDates = () => {
    const now = new Date();
    const startDate = isSameDayBookingAllowed(now) ? new Date() : addDays(new Date(), 1);
    const endDate = addDays(startDate, 1);
    return { startDate, endDate };
  };
  
  const defaultPeriod = [
    {
      startDate: reservation_period.start
        ? new Date(reservation_period.start * 1000)
        : getDefaultDates().startDate,
      endDate: reservation_period.end
        ? new Date(reservation_period.end * 1000)
        : getDefaultDates().endDate,
      key: "selection",
      color: "#5b1deeaa",
    },
  ];

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('dates'); // 'dates' or 'months'
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    // Ensure start date is initialized with at least today
    return defaultPeriod[0].startDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    // Ensure end date is at least 1 day after start date
    const start = defaultPeriod[0].startDate;
    const end = defaultPeriod[0].endDate;
    return end > start ? end : addDays(start, 1);
  });
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' or 'end'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  /**
   * Extract blocked dates from property object (same approach as host calendar)
   * This includes: internal bookings, external calendar syncs, and manual blocks
   */
  const extractBlockedDatesFromProperty = useCallback((property) => {
    if (!property) {
      console.log(`📅 [PickDateWithBlockedDates] No property provided, clearing blocked dates`);
      setBlockedDateRanges([]);
      setDisabledDates([]);
      return;
    }

    console.log(`📅 [PickDateWithBlockedDates] Extracting blocked dates for property ${property.token_id}`);
    const blockedRanges = [];
    
    // 1. Internal bookings (CodedEstate platform)
    // ✅ MATCH HOST CALENDAR: Include all rentals (don't filter cancelled/failed for blocking purposes)
    const internalBookings = [];
    if (property.rentals?.rentals && Array.isArray(property.rentals.rentals)) {
      console.log(`📅 [PickDateWithBlockedDates] Checking ${property.rentals.rentals.length} internal rentals`);
      property.rentals.rentals.forEach((rental, index) => {
        // Log full rental object for debugging
        console.log(`📅 [PickDateWithBlockedDates] 📋 Rental ${index} full object:`, JSON.stringify(rental, null, 2));
        console.log(`📅 [PickDateWithBlockedDates] Rental ${index} summary:`, {
          address: rental.address,
          renting_period: rental.renting_period,
          cancelled: rental.cancelled,
          sync_status: rental.sync_status,
          approved: rental.approved,
          rejected: rental.rejected
        });
        
        // Validate renting_period exists and has valid dates
        if (!rental.renting_period || !Array.isArray(rental.renting_period) || rental.renting_period.length < 2) {
          console.log(`📅 [PickDateWithBlockedDates] ⚠️ Skipping rental ${index} - invalid renting_period:`, rental.renting_period);
          return;
        }
        
        const startTimestamp = parseInt(rental.renting_period[0]);
        const endTimestamp = parseInt(rental.renting_period[1]);
        
        if (isNaN(startTimestamp) || isNaN(endTimestamp)) {
          console.log(`📅 [PickDateWithBlockedDates] ⚠️ Skipping rental ${index} - invalid timestamps:`, {
            start: rental.renting_period[0],
            end: rental.renting_period[1]
          });
          return;
        }
        
        const startDate = new Date(startTimestamp * 1000);
        const endDate = new Date(endTimestamp * 1000);
        
        // Validate dates
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.log(`📅 [PickDateWithBlockedDates] ⚠️ Skipping rental ${index} - invalid dates after conversion:`, {
            startTimestamp,
            endTimestamp,
            startDate: startDate.toString(),
            endDate: endDate.toString()
          });
          return;
        }
        
        // ✅ Only skip if explicitly cancelled (cancelled === true) OR explicitly failed (sync_status === "failed")
        // Don't skip if cancelled is undefined/null/false or sync_status is not "failed"
        const isCancelled = rental.cancelled === true;
        const isFailed = rental.sync_status === "failed";
        
        if (isCancelled || isFailed) {
          console.log(`📅 [PickDateWithBlockedDates] ⚠️ Skipping rental ${index} - cancelled: ${isCancelled}, failed: ${isFailed}`);
          return;
        }
        
        console.log(`📅 [PickDateWithBlockedDates] ✅ Adding rental ${index}:`, {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          startTimestamp,
          endTimestamp,
          period: rental.renting_period,
          cancelled: rental.cancelled,
          sync_status: rental.sync_status
        });
        
        internalBookings.push({
          startDate,
          endDate,
          type: "booking",
          source: "CodedEstate"
        });
      });
      console.log(`📅 [PickDateWithBlockedDates] Found ${internalBookings.length} active internal bookings (out of ${property.rentals.rentals.length} total)`);
      blockedRanges.push(...internalBookings);
    } else {
      console.log(`📅 [PickDateWithBlockedDates] No internal rentals found (rentals:`, property.rentals);
    }
    
    // 2. External platform bookings (Airbnb, Booking.com, etc.)
    const externalBookings = [];
    if (property.icalSync?.externalBlockedDates && Array.isArray(property.icalSync.externalBlockedDates)) {
      console.log(`📅 [PickDateWithBlockedDates] Checking ${property.icalSync.externalBlockedDates.length} external blocked dates`);
      property.icalSync.externalBlockedDates.forEach(blocked => {
        externalBookings.push({
          startDate: new Date(blocked.startDate),
          endDate: new Date(blocked.endDate),
          type: "external",
          source: blocked.platform || "External Platform"
        });
      });
      console.log(`📅 [PickDateWithBlockedDates] Found ${externalBookings.length} external blocked date ranges`);
      blockedRanges.push(...externalBookings);
    } else {
      console.log(`📅 [PickDateWithBlockedDates] No external blocked dates found (icalSync:`, property.icalSync);
    }
    
    // 3. Manual blocks
    const manualBlocks = [];
    if (property.manuallyBlockedDates && Array.isArray(property.manuallyBlockedDates)) {
      console.log(`📅 [PickDateWithBlockedDates] Checking ${property.manuallyBlockedDates.length} manual blocks`);
      property.manuallyBlockedDates.forEach(block => {
        manualBlocks.push({
          startDate: new Date(block.startDate),
          endDate: new Date(block.endDate),
          type: "manual",
          source: "Manually Blocked"
        });
      });
      console.log(`📅 [PickDateWithBlockedDates] Found ${manualBlocks.length} manual blocks`);
      blockedRanges.push(...manualBlocks);
    } else {
      console.log(`📅 [PickDateWithBlockedDates] No manual blocks found (manuallyBlockedDates:`, property.manuallyBlockedDates);
    }

    console.log(`📅 [PickDateWithBlockedDates] SUMMARY - Total blocked date ranges: ${blockedRanges.length}`, {
      internalBookings: internalBookings.length,
      externalBookings: externalBookings.length,
      manualBlocks: manualBlocks.length,
      ranges: blockedRanges.map(r => ({
        type: r.type,
        source: r.source,
        start: r.startDate.toISOString().split('T')[0],
        end: r.endDate.toISOString().split('T')[0]
      }))
    });
    
    setBlockedDateRanges(blockedRanges);
    
    // Convert blocked date ranges to individual disabled dates
    const disabledDatesList = [];
    blockedRanges.forEach((range) => {
      try {
        const start = new Date(range.startDate);
        const end = new Date(range.endDate);
        
        // Validate dates
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          console.warn(`📅 [PickDateWithBlockedDates] Invalid date range:`, range);
          return;
        }
        
        // Normalize dates to start of day (local time)
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        
        // Add day before first blocked date - user can't check-in here (checkout would fall on blocked)
        const dayBeforeBlock = addDays(start, -1);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dayBeforeBlock >= today) {
          disabledDatesList.push(new Date(dayBeforeBlock));
        }
        
        // Add all dates in the range (inclusive)
        let currentDate = new Date(start);
        while (currentDate <= end) {
          disabledDatesList.push(new Date(currentDate));
          currentDate = addDays(currentDate, 1);
        }
      } catch (error) {
        console.warn(`📅 [PickDateWithBlockedDates] Error processing blocked date range:`, range, error);
      }
    });
    
    // Remove duplicates and sort
    const uniqueDisabledDates = Array.from(
      new Set(disabledDatesList.map(d => d.getTime()))
    )
      .map(time => new Date(time))
      .sort((a, b) => a - b);
    
    console.log(`📅 [PickDateWithBlockedDates] ✅ Converted ${blockedRanges.length} ranges to ${uniqueDisabledDates.length} individual disabled dates`);
    
    if (uniqueDisabledDates.length > 0) {
      console.log(`📅 [PickDateWithBlockedDates] 📌 Sample disabled dates (first 10):`, 
        uniqueDisabledDates.slice(0, 10).map(d => d.toISOString().split('T')[0])
      );
      console.log(`📅 [PickDateWithBlockedDates] 📌 Blocked dates range: ${uniqueDisabledDates[0].toISOString().split('T')[0]} to ${uniqueDisabledDates[uniqueDisabledDates.length - 1].toISOString().split('T')[0]}`);
    } else {
      console.log(`📅 [PickDateWithBlockedDates] ⚠️ NO BLOCKED DATES FOUND - Calendar will show all dates as available`);
    }
    
    setDisabledDates(uniqueDisabledDates);
    setLoadingBlockedDates(false);
  }, []);

  // Extract blocked dates when token changes (token is fetched via updateToken which calls property/getProperty)
  useEffect(() => {
    if (token) {
      console.log(`📅 [PickDateWithBlockedDates] Token loaded/changed, extracting blocked dates...`);
      console.log(`📅 [PickDateWithBlockedDates] Token object:`, {
        token_id: token.token_id,
        hasRentals: !!token.rentals,
        rentalsCount: token.rentals?.rentals?.length || 0,
        rentals: token.rentals?.rentals,
        hasIcalSync: !!token.icalSync,
        icalSync: token.icalSync,
        externalBlockedCount: token.icalSync?.externalBlockedDates?.length || 0,
        externalBlockedDates: token.icalSync?.externalBlockedDates,
        hasManualBlocks: !!token.manuallyBlockedDates,
        manuallyBlockedDates: token.manuallyBlockedDates,
        manualBlocksCount: token.manuallyBlockedDates?.length || 0
      });
      
      // Log individual rental periods for debugging
      if (token.rentals?.rentals && Array.isArray(token.rentals.rentals)) {
        console.log(`📅 [PickDateWithBlockedDates] Internal rentals details:`, 
          token.rentals.rentals.map(r => ({
            address: r.address,
            period: r.renting_period,
            cancelled: r.cancelled,
            sync_status: r.sync_status,
            startDate: r.renting_period?.[0] ? new Date(parseInt(r.renting_period[0]) * 1000).toISOString().split('T')[0] : null,
            endDate: r.renting_period?.[1] ? new Date(parseInt(r.renting_period[1]) * 1000).toISOString().split('T')[0] : null
          }))
        );
      }
      
      setLoadingBlockedDates(true);
      extractBlockedDatesFromProperty(token);
    } else {
      console.log(`📅 [PickDateWithBlockedDates] ⚠️ No token yet, clearing blocked dates`);
      setDisabledDates([]);
      setBlockedDateRanges([]);
      setLoadingBlockedDates(false);
    }
  }, [token, extractBlockedDatesFromProperty]);

  const setFirstAvailableDay = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Start from today or tomorrow based on same-day cutoff (11:30)
    const startFrom = isSameDayBookingAllowed(today) ? 0 : 1;

    if (disabledDates.length === 0) {
      return addDays(today, startFrom);
    }

    // Find first date (from startFrom onward) that is NOT in disabledDates
    for (let i = startFrom; i < 10000; i++) {
      const candidate = addDays(today, i);
      candidate.setHours(0, 0, 0, 0);
      const isBlocked = disabledDates.some((d) => {
        const dd = new Date(d);
        dd.setHours(0, 0, 0, 0);
        return dd.getTime() === candidate.getTime();
      });
      if (!isBlocked) return candidate;
    }
    return addDays(today, startFrom);
  };


  useEffect(() => {
    setFirstDay(setFirstAvailableDay());
  }, [disabledDates]);

  // Helper to check if a date is in disabledDates (inline to avoid dependency issues)
  const isDateInDisabledList = (date, disabledList) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return disabledList.some((disabled) => {
      const dd = new Date(disabled);
      dd.setHours(0, 0, 0, 0);
      return dd.getTime() === d.getTime();
    });
  };

  // When blocked dates load, if current selection includes blocked dates, switch to first available range
  useEffect(() => {
    if (disabledDates.length === 0) return;
    const firstAvailable = setFirstAvailableDay();
    if (!firstAvailable) return;

    const start = new Date(period[0]?.startDate || selectedStartDate);
    const end = new Date(period[0]?.endDate || selectedEndDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Check if any date in range is blocked
    let hasBlocked = false;
    let current = new Date(start);
    while (current <= end) {
      if (isDateInDisabledList(current, disabledDates)) {
        hasBlocked = true;
        break;
      }
      current = addDays(current, 1);
    }

    if (hasBlocked) {
      const newStart = new Date(firstAvailable);
      newStart.setHours(0, 0, 0, 0);

      // Sanity: newStart must not be blocked (setFirstAvailableDay ensures this)
      if (isDateInDisabledList(newStart, disabledDates)) return;

      // Find valid end such that range [newStart, checkEnd] has no blocked dates
      let checkEnd = addDays(newStart, 1);
      for (let i = 0; i < 365; i++) {
        let blockedInRange = false;
        let c = new Date(newStart);
        while (c <= checkEnd) {
          if (isDateInDisabledList(c, disabledDates)) {
            blockedInRange = true;
            break;
          }
          c = addDays(c, 1);
        }
        if (!blockedInRange) break;
        checkEnd = addDays(checkEnd, 1);
      }
      setSelectedStartDate(newStart);
      setSelectedEndDate(checkEnd);
      setPeriod([{
        startDate: newStart,
        endDate: checkEnd,
        key: "selection",
        color: "#5b1deeaa",
      }]);
      setCurrentDate(new Date(newStart.getFullYear(), newStart.getMonth(), 1));
    }
  }, [disabledDates]);

  const [period, setPeriod] = useState(defaultPeriod);

  useEffect(() => {
    dispatch(
      setReservationPeriod({
        start: Math.floor(new Date(period[0].startDate).getTime() / 1000),
        end: Math.floor(new Date(period[0].endDate).getTime() / 1000),
      })
    );
  }, [period]);

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Adjust for Monday as first day of week (0 = Monday, 6 = Sunday)
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    return { daysInMonth, startingDayOfWeek: adjustedStartingDay };
  };

  const getPreviousMonth = () => {
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    // Compare only year and month, not the full date
    const today = new Date();
    const prevYear = prevMonth.getFullYear();
    const prevMonthIndex = prevMonth.getMonth();
    const currentYear = today.getFullYear();
    const currentMonthIndex = today.getMonth();
    
    // Allow navigation if previous month is not before current month
    if (prevYear > currentYear || (prevYear === currentYear && prevMonthIndex >= currentMonthIndex)) {
      setCurrentDate(prevMonth);
    }
  };

  const getNextMonth = () => {
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentDate(nextMonth);
  };

  // Helper: Get blocked dates that fall within a date range (for checkout validation)
  const getBlockedDatesInRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const blockedInRange = [];
    let current = new Date(start);
    while (current <= end) {
      const checkDate = new Date(current);
      checkDate.setHours(0, 0, 0, 0);
      const isBlocked = disabledDates.some((d) => {
        const dNorm = new Date(d);
        dNorm.setHours(0, 0, 0, 0);
        return dNorm.getTime() === checkDate.getTime();
      });
      if (isBlocked) blockedInRange.push(new Date(current));
      current = addDays(current, 1);
    }
    return blockedInRange;
  };

  // Helper: Find a valid end date such that the range [start, end] contains no blocked dates
  const findValidEndDate = (startDate) => {
    let checkEnd = addDays(startDate, 1);
    const maxAttempts = 365;
    let attempts = 0;
    while (attempts < maxAttempts) {
      const blocked = getBlockedDatesInRange(startDate, checkEnd);
      if (blocked.length === 0) return checkEnd;
      // Skip past the blocked dates - next end = day after last blocked in range
      const lastBlocked = blocked[blocked.length - 1];
      checkEnd = addDays(lastBlocked, 1);
      attempts++;
    }
    return null;
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    // Don't allow selection if date is disabled
    if (isDateDisabled(day)) {
      return;
    }
    
    if (selectionMode === 'start') {
      // Check if this start date has a valid end (range with no blocked dates)
      const validEnd = findValidEndDate(clickedDate);
      if (!validEnd) {
        setBlockedDatesInRange(getBlockedDatesInRange(clickedDate, addDays(clickedDate, 1)));
        setShowBlockedDatesWarningModal(true);
        return;
      }
      setSelectedStartDate(clickedDate);
      setSelectedEndDate(validEnd);
      setSelectionMode('end');
    } else {
      // When selecting end date - validate range doesn't include blocked dates
      if (clickedDate > selectedStartDate) {
        const blocked = getBlockedDatesInRange(selectedStartDate, clickedDate);
        if (blocked.length > 0) {
          setBlockedDatesInRange(blocked);
          setShowBlockedDatesWarningModal(true);
          return;
        }
        setSelectedEndDate(clickedDate);
        setSelectionMode('start');
      } else {
        // If clicked date is before or same as start date, make it the new start date
        const validEnd = findValidEndDate(clickedDate);
        if (!validEnd) {
          setBlockedDatesInRange(getBlockedDatesInRange(clickedDate, addDays(clickedDate, 1)));
          setShowBlockedDatesWarningModal(true);
          return;
        }
        setSelectedStartDate(clickedDate);
        setSelectedEndDate(validEnd);
        setSelectionMode('end');
      }
    }
  };

  const handleApply = () => {
    // Validate that selected range doesn't include blocked dates
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    
    // Check if any date in the range is blocked
    const startCheck = new Date(start);
    startCheck.setHours(0, 0, 0, 0);
    const endCheck = new Date(end);
    endCheck.setHours(0, 0, 0, 0);
    
    let currentDate = new Date(startCheck);
    while (currentDate <= endCheck) {
      const dateToCheck = new Date(currentDate);
      dateToCheck.setHours(0, 0, 0, 0);
      
      const isBlocked = disabledDates.some(disabledDate => {
        try {
          const disabled = new Date(disabledDate);
          disabled.setHours(0, 0, 0, 0);
          return disabled.getTime() === dateToCheck.getTime();
        } catch (error) {
          return false;
        }
      });
      
      if (isBlocked) {
        const blockedInRange = getBlockedDatesInRange(start, end);
        setBlockedDatesInRange(blockedInRange);
        setShowBlockedDatesWarningModal(true);
        return;
      }
      
      currentDate = addDays(currentDate, 1);
    }
    
    const newPeriod = [{
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      key: "selection",
      color: "#5b1deeaa",
    }];
    setPeriod(newPeriod);
    setShowModal(false);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const newDate = new Date(selectedYear, month, 1);
    setCurrentDate(newDate);
    setViewMode('dates');
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    const newDate = new Date(year, selectedMonth, 1);
    setCurrentDate(newDate);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isDateSelected = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const isStartDate = selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
    
    const isEndDate = selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
    
    const isInRange = selectedStartDate && selectedEndDate &&
           dateToCheck >= selectedStartDate && dateToCheck <= selectedEndDate;
    
    return isStartDate || isEndDate || isInRange;
  };

  const isDateStart = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
  };

  const isDateEnd = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
  };

  const isDateDisabled = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    dateToCheck.setHours(0, 0, 0, 0); // Normalize to start of day
    
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateToCheck < today) {
      return true;
    }
    
    // Check if date is in disabledDates array (blocked by bookings or external platforms)
    const isDisabled = disabledDates.some(disabledDate => {
      try {
        const disabledDay = new Date(disabledDate);
        disabledDay.setHours(0, 0, 0, 0); // Normalize to start of day
        
        // Compare by timestamp for accurate comparison
        const matches = disabledDay.getTime() === dateToCheck.getTime();
        if (matches) {
          // Log when we find a blocked date match (only once per render cycle to avoid spam)
          if (!window._lastBlockedDateLog || window._lastBlockedDateLog !== dateToCheck.toISOString()) {
            console.log(`📅 [PickDateWithBlockedDates] 🚫 Date ${dateToCheck.toISOString().split('T')[0]} is BLOCKED (will show horizontal black line)`);
            window._lastBlockedDateLog = dateToCheck.toISOString();
          }
        }
        return matches;
      } catch (error) {
        console.warn(`📅 [PickDateWithBlockedDates] Error comparing dates:`, error);
        return false;
      }
    });
    
    return isDisabled;
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  // Get previous month's days to fill the grid
  const getPreviousMonthDays = () => {
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    const days = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(daysInPrevMonth - i);
    }
    return days;
  };

  const previousMonthDays = getPreviousMonthDays();

  return (
    <>
      {/* Price and Available From Section - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0">
        <div>
          <div className="font-semibold text-sm sm:text-base">Price</div>
          <div className="flex items-center gap-[6px] sm:gap-[8px]">
            <div className="scale-75 sm:scale-100">
              <NUSDIcon />
            </div>
            <div className="text-[#5b1dee] text-[18px] sm:text-[20px]">
              {token?.short?.price_per_day}
            </div>
            <div className="text-sm sm:text-base">USDC</div>
            <div className="text-[#959595] text-sm sm:text-base">/night</div>
          </div>
        </div>
        <div className="space-y-[4px] text-center sm:text-right">
          <div className="p-[6px] w-max mx-auto sm:mx-0 rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <HomeIcon />
          </div>
          <div className="font-semibold text-sm sm:text-base">Available from</div>
          <div className="font-normal text-sm sm:text-base">
            {firstDay?.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </div>
        </div>
      </div>

      {/* Auto Approval Section - Responsive */}
      <div className="flex justify-center sm:justify-end">
        <div className="space-y-[10px] flex flex-col items-center sm:items-end">
          <div className="flex items-center gap-[8px] sm:gap-[10px]">
            <div className="font-semibold text-sm sm:text-base">Auto approval</div>
            <div className="scale-75 sm:scale-100">
              <Toggle status={token?.short?.auto_approve} disableHover={true} />
            </div>
          </div>
          <div className="text-center sm:text-right text-xs sm:text-sm max-w-[300px] sm:max-w-none">
            {token?.short?.auto_approve
              ? "Your rental will be activated immediately once you make reservation"
              : "Your rental will be activated once host approve your reservation"}
          </div>
        </div>
      </div>

      {/* Check-in/Check-out Section - Responsive */}
      <div className="flex flex-col sm:flex-row justify-between gap-[10px] sm:gap-[20px] w-full">
        <div className="w-full space-y-[10px]">
          <div className="font-semibold text-sm sm:text-base">Check in</div>
          <div
            onClick={() => {
              console.log(`📅 [PickDateWithBlockedDates] 📱 Calendar modal opened (checkout)`);
              console.log(`📅 [PickDateWithBlockedDates] Current blocked dates state:`, {
                totalBlockedRanges: blockedDateRanges.length,
                totalDisabledDates: disabledDates.length,
                loading: loadingBlockedDates
              });
              setShowModal(true);
            }}
            className="cursor-pointer hover:bg-[#f6f6f6] px-[12px] flex justify-between text-[#5b1dee] w-full py-[12px] sm:py-[14px] rounded-[16px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <div className="text-sm sm:text-base">
              {new Date(period[0]?.startDate)?.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="scale-75 sm:scale-100">
              <CalenderIcon />
            </div>
          </div>
        </div>
        <div className="w-full space-y-[10px]">
          <div className="font-semibold text-sm sm:text-base">Check out</div>
          <div
            onClick={() => {
              console.log(`📅 [PickDateWithBlockedDates] 📱 Calendar modal opened (checkout)`);
              console.log(`📅 [PickDateWithBlockedDates] Current blocked dates state:`, {
                totalBlockedRanges: blockedDateRanges.length,
                totalDisabledDates: disabledDates.length,
                loading: loadingBlockedDates
              });
              setShowModal(true);
            }}
            className="cursor-pointer hover:bg-[#f6f6f6] px-[12px] flex justify-between text-[#5b1dee] w-full py-[12px] sm:py-[14px] rounded-[16px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <div className="text-sm sm:text-base">
              {new Date(period[0].endDate)?.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="scale-75 sm:scale-100">
              <CalenderIcon />
            </div>
          </div>
        </div>
      </div>

      {/* Functional Calendar Modal */}
      <Modal
        open={showModal}
        center
        onClose={() => setShowModal(false)}
        showCloseIcon={false}
        classNames={{
          modal: "w-[95vw] max-w-[400px] rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-0 overflow-hidden bg-white",
          overlay: "z-[9999999]",
          modalContainer: "z-[9999999]",
        }}
        styles={{
          modal: {
            zIndex: 9999999,
          },
          overlay: {
            zIndex: 9999999,
          },
        }}
      >
        <div className="w-full h-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <button
              onClick={() => setShowModal(false)}
              className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold">Select Dates</h2>
            <div className="w-10"></div>
          </div>

          {/* Toggle between Dates and Months */}
          <div className="p-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('dates')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'dates' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>Dates</span>
                  {viewMode === 'dates' && (
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  )}
                </div>
              </button>
              <button
                onClick={() => setViewMode('months')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'months' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600'
                }`}
              >
                Months
              </button>
            </div>
          </div>

          {/* Content based on view mode */}
          {viewMode === 'dates' ? (
            <>
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between px-4 mb-4">
                <button
                  onClick={getPreviousMonth}
                  className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                  disabled={(() => {
                    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
                    const today = new Date();
                    const prevYear = prevMonth.getFullYear();
                    const prevMonthIndex = prevMonth.getMonth();
                    const currentYear = today.getFullYear();
                    const currentMonthIndex = today.getMonth();
                    return prevYear < currentYear || (prevYear === currentYear && prevMonthIndex < currentMonthIndex);
                  })()}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-lg font-semibold">{formatMonthYear(currentDate)}</h3>
                <button
                  onClick={getNextMonth}
                  className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Selection Mode Indicator */}
              <div className="px-4 mb-4">
                <div className="text-sm text-[#666666] text-center">
                  {selectionMode === 'start' ? 'Select check-in date' : 'Select check-out date'}
                </div>
                {loadingBlockedDates && (
                  <div className="text-xs text-[#959595] text-center mt-1">
                    Loading availability...
                  </div>
                )}
              </div>

              {/* Calendar Grid */}
              <div className="flex-1 px-4 pb-6 overflow-y-auto">
                {/* Days of week header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((day) => (
                    <div key={day} className="text-center text-sm text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Previous month days */}
                  {previousMonthDays.map((day) => (
                    <div key={`prev-${day}`} className="text-center py-2 text-gray-300">
                      {day}
                    </div>
                  ))}

                  {/* Current month days */}
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                    const isSelected = isDateSelected(day);
                    const isStart = isDateStart(day);
                    const isEnd = isDateEnd(day);
                    const isDisabled = isDateDisabled(day);
                    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                    const isPast = dateToCheck < new Date();
                    const isBlocked = !isPast && isDisabled; // Blocked by booking/external platform/manual block
                    
                    // Log calendar render stats once per month
                    if (day === 1 && !window._calendarRenderLog) {
                      const blockedCount = Array.from({ length: daysInMonth }, (_, i) => {
                        const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1);
                        checkDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (checkDate < today) return false;
                        return disabledDates.some(d => {
                          const dDate = new Date(d);
                          dDate.setHours(0, 0, 0, 0);
                          return dDate.getTime() === checkDate.getTime();
                        });
                      }).filter(Boolean).length;
                      console.log(`📅 [PickDateWithBlockedDates] 📊 Calendar render for ${formatMonthYear(currentDate)}: ${blockedCount}/${daysInMonth} dates are blocked (will show horizontal black lines)`);
                      window._calendarRenderLog = true;
                      setTimeout(() => { window._calendarRenderLog = false; }, 1000);
                    }
                    
                    return (
                      <button
                        key={day}
                        onClick={() => !isDisabled && handleDateClick(day)}
                        disabled={isDisabled}
                        className={`relative text-center py-2 rounded-full transition-colors touch-manipulation ${
                          isStart || isEnd
                            ? 'bg-[#5b1dee] text-white'
                            : isSelected
                            ? 'bg-[#5b1dee] bg-opacity-20 text-[#5b1dee]'
                            : isDisabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {day}
                        {/* Horizontal black line for blocked dates - limited to number width */}
                        {isBlocked && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-[70%] h-[2px] bg-black opacity-80"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}

                  {/* Next month days to fill the grid */}
                  {(() => {
                    const totalCells = 42; // 6 rows * 7 columns
                    const usedCells = previousMonthDays.length + daysInMonth;
                    const remainingCells = Math.max(0, totalCells - usedCells);
                    
                    return Array.from({ length: remainingCells }, (_, i) => (
                      <div key={`next-${i + 1}`} className="text-center py-2 text-gray-300">
                        {i + 1}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          ) : (
            /* Months and Years Selection */
            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {/* Year Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Year</h4>
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: 5 }, (_, i) => selectedYear - 2 + i).map((year) => (
                    <button
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                        year === selectedYear
                          ? 'bg-[#5b1dee] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Month Selection */}
              <div>
                <h4 className="text-lg font-semibold mb-3">Month</h4>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    'January', 'February', 'March',
                    'April', 'May', 'June',
                    'July', 'August', 'September',
                    'October', 'November', 'December'
                  ].map((month, index) => (
                    <button
                      key={month}
                      onClick={() => handleMonthSelect(index)}
                      className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                        index === selectedMonth && selectedYear === currentDate.getFullYear()
                          ? 'bg-[#5b1dee] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {month}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Apply Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleApply}
              className="w-full bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee] text-white py-4 px-6 rounded-[12px] font-semibold text-lg shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px] active:bg-[#5b1dee] touch-manipulation flex items-center justify-center gap-2 transition-all duration-200"
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>

      {/* Blocked dates warning modal */}
      <Modal
        open={showBlockedDatesWarningModal}
        center
        onClose={() => setShowBlockedDatesWarningModal(false)}
        showCloseIcon={true}
        classNames={{
          modal: "w-[90vw] max-w-[320px] rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-0 overflow-hidden bg-white",
          overlay: "z-[99999999]",
          modalContainer: "z-[99999999]",
        }}
        styles={{
          modal: { zIndex: 99999999 },
          overlay: { zIndex: 99999999 },
        }}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Blocked dates in selection
          </h3>
          <p className="text-gray-600 mb-4">
            Your selected dates include blocked dates. Please change your dates to avoid the following unavailable dates:
          </p>
          {blockedDatesInRange.length > 0 && (
            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Blocked dates:
              </div>
              <div className="text-sm text-gray-600">
                {blockedDatesInRange
                  .slice(0, 7)
                  .map((d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }))
                  .join(", ")}
                {blockedDatesInRange.length > 7 && ` and ${blockedDatesInRange.length - 7} more`}
              </div>
            </div>
          )}
          <p className="text-sm text-gray-500 mb-4">
            Please select a different check-in or check-out date to avoid these blocked dates.
          </p>
          <button
            onClick={() => setShowBlockedDatesWarningModal(false)}
            className="w-full bg-[#5b1dee] text-white py-3 px-6 rounded-[12px] font-semibold hover:bg-[#4a18c7] transition-colors"
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};
